import React from "react";
import { Box, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ReactComponent as ChevronLeftIcon } from "../../assets/chevron-left-double.svg";
import { ReactComponent as MenuIcon } from "../../assets/menu.svg";
import { SidebarLogo } from "./SidebarLogo";
import { useNavigate } from "react-router-dom";

interface SidebarHeaderProps {
  sidebarOpen: boolean;
  mobileView?: boolean;
  isPortalDesign: boolean;
  sidebarShade: string;
  sidebarTextColor: string;
  onOpen: () => void;
  onClose: () => void;
}

const StyledIconButton: React.FC<
  React.ComponentProps<typeof IconButton> & {
    sidebarShade: string;
    sidebarTextColor?: string;
  }
> = ({ sidebarShade, sidebarTextColor, children, sx, ...props }) => (
  <IconButton
    {...props}
    sx={{
      backgroundColor: "transparent",
      border: "none",
      color: sidebarTextColor,
      "&:hover": {
        backgroundColor: `${sidebarShade} !important`,
      },
      ...sx,
    }}
  >
    {children}
  </IconButton>
);

export const SidebarHeader: React.FC<SidebarHeaderProps> = ({
  sidebarOpen,
  mobileView,
  isPortalDesign,
  sidebarShade,
  sidebarTextColor,
  onOpen,
  onClose,
}) => {
  const navigate = useNavigate();

  if (!sidebarOpen) {
    return (
      <StyledIconButton
        sidebarShade={sidebarShade}
        sidebarTextColor={sidebarTextColor}
        aria-label="open drawer"
        onClick={onOpen}
        sx={{ padding: "16px !important" }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            display: "flex",
            "& svg": { width: 24, height: 24 },
          }}
        >
          <MenuIcon />
        </Box>
      </StyledIconButton>
    );
  }

  if (mobileView) {
    return (
      <>
        <SidebarLogo mobileView={mobileView} />
        <StyledIconButton
          sidebarShade={sidebarShade}
          sidebarTextColor={sidebarTextColor}
          onClick={onClose}
        >
          <CloseIcon sx={{ color: "inherit" }} />
        </StyledIconButton>
      </>
    );
  }

  if (isPortalDesign) {
    return (
      <>
        <Button 
          data-nav="back-to-portals"
          startIcon={<ArrowBackIcon fontSize="small" />}
          variant="text"
          onClick={() => navigate("/portal-configuration")}
          sx={{
            color: sidebarTextColor,
            minWidth: 0,
            flex: 1,
            justifyContent: "flex-start",
            textTransform: "none",
            overflow: "hidden",
            "&:hover": {
              backgroundColor: `${sidebarShade} !important`,
            },
            "& .MuiButton-startIcon": {
              flexShrink: 0,
            },
          }}
        >
          <Box
            component="span"
            sx={{
              whiteSpace: "nowrap",
              opacity: sidebarOpen ? 1 : 0,
              transition: "opacity 150ms ease",
            }}
          >
            Back to Portals
          </Box>
        </Button>
        <StyledIconButton
          sidebarShade={sidebarShade}
          sidebarTextColor={sidebarTextColor}
          onClick={onClose}
        >
          <ChevronLeftIcon style={{ width: 24, height: 24 }} />
        </StyledIconButton>
      </>
    );
  }

  return (
    <>
      <Box sx={{ flex: 1 }} />
      <StyledIconButton
        sidebarShade={sidebarShade}
        sidebarTextColor={sidebarTextColor}
        onClick={onClose}
      >
        <ChevronLeftIcon style={{ width: 24, height: 24 }} />
      </StyledIconButton>
    </>
  );
};
